import { inject } from "@angular/core";
import { Router, Routes } from "@angular/router";
import { featureFlagGuard } from "@app/core/guards/feature-flag.guard";
import {
  anyPermissionGuard,
  permissionGuard,
} from "@app/core/guards/permission.guard";
import { MenuMeta } from "@app/core/models/menu-meta";
import { Permissions } from "@app/core/models/permissions.generated";
import {
  APP_ROUTES,
  ROUTE_SEGMENTS,
} from "@shared/constants/app-routes.constants";
import {
  VDO_CAMPAIGN_FLAG,
  VDO_SHELF_FLAG,
  VdoChannelResolver,
} from "@app/shared/services/vdo-channel";

/**
 * Management top-level menu routes.
 *
 * Each route declares both `canActivate` and `data.menu` with the same
 * `Permissions.*` constant — the guard is a closure (not introspectable),
 * while `RouterMenuService` needs the perm string to decide sidebar visibility.
 */
export const managementRoutes: Routes = [
  {
    path: ROUTE_SEGMENTS.AGREEMENT,
    canActivate: [permissionGuard(Permissions.AgreementRead)],
    data: {
      menu: {
        label: "Agreement",
        icon: "handshake",
        requiredPermission: Permissions.AgreementRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./agreement/agreement.routes").then((m) => m.agreementRoutes),
  },
  {
    path: ROUTE_SEGMENTS.BANNER,
    canActivate: [permissionGuard(Permissions.BannerRead)],
    data: {
      menu: {
        label: "Banner",
        icon: "flag",
        requiredPermission: Permissions.BannerRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./banner-management/banner-management.route").then(
        (m) => m.bannerManagementRoutes,
      ),
  },
  {
    path: ROUTE_SEGMENTS.BANNER_REDESIGN,
    canActivate: [permissionGuard(Permissions.BannerRead)],
    loadChildren: () =>
      import("./banner-management/banner-management.route").then(
        (m) => m.bannerRedesignRoutes,
      ),
  },
  {
    path: ROUTE_SEGMENTS.CAMPAIGN,
    canActivate: [permissionGuard(Permissions.CampaignRead)],
    data: {
      menu: {
        label: "Campaign",
        icon: "campaign",
        requiredPermission: Permissions.CampaignRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./campaign/campaign.routes").then((m) => m.campaignRoutes),
  },
  {
    // Device Campaign for MC is a Multi-Channel variant of the Campaign feature
    // with its own campaignMc:* resource, so it grants independently of the
    // main Campaign menu.
    path: ROUTE_SEGMENTS.CAMPAIGN_MC,
    canActivate: [permissionGuard(Permissions.CampaignMcRead)],
    data: {
      menu: {
        label: "Device Campaign for MC",
        icon: "campaign",
        requiredPermission: Permissions.CampaignMcRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./campaign-mc/campaign-mc.routes").then(
        (m) => m.campaignMcRoutes,
      ),
  },
  {
    path: ROUTE_SEGMENTS.CONDITION_TEMPLATE,
    // Separate feature from Agreement — more fields + different business (BRD
    // §2.1.5).
    canActivate: [permissionGuard(Permissions.ConditionTemplateRead)],
    data: {
      menu: {
        label: "Condition Template",
        icon: "rule",
        requiredPermission: Permissions.ConditionTemplateRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./condition-template/condition-template.routes").then(
        (m) => m.conditionTemplateRoutes,
      ),
  },
  {
    path: ROUTE_SEGMENTS.ENRICHMENT,
    canActivate: [permissionGuard(Permissions.EnrichmentRead)],
    data: {
      menu: {
        label: "Enrichment",
        icon: "auto_awesome",
        requiredPermission: Permissions.EnrichmentRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./enrichment/enrichment.route").then((m) => m.enrichmentRoutes),
  },
  {
    path: ROUTE_SEGMENTS.ENRICHMENT_PRICE,
    canActivate: [
      featureFlagGuard("enableEnrichmentPriceMode"),
      permissionGuard(Permissions.EnrichmentRead),
    ],
    loadChildren: () =>
      import("./enrichment-price/enrichment-price.route").then(
        (m) => m.enrichmentPriceRoutes,
      ),
  },
  // Both Enrichment modes open Mobile packages here, so it sits outside
  // either mode's flag and redirect guard.
  {
    path: ROUTE_SEGMENTS.ENRICHMENT_PACKAGE,
    canActivate: [permissionGuard(Permissions.EnrichmentRead)],
    loadChildren: () =>
      import("./enrichment-package/enrichment-package.routes").then(
        (m) => m.enrichmentPackageRoutes,
      ),
  },
  {
    path: ROUTE_SEGMENTS.INTERNET_ROAMING,
    canActivate: [permissionGuard(Permissions.InternetRoamingRead)],
    data: {
      menu: {
        label: "Internet Roaming",
        icon: "travel_explore",
        group: true,
        requiredPermission: Permissions.InternetRoamingRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./internet-roaming/internet-roaming.routes").then(
        (m) => m.internetRoamingRoutes,
      ),
  },
  {
    path: ROUTE_SEGMENTS.LIFESTYLE,
    canActivate: [permissionGuard(Permissions.LifeStyleRead)],
    data: {
      menu: {
        label: "Lifestyle",
        icon: "spa",
        requiredPermission: Permissions.LifeStyleRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./lifestyle/lifestyle.routes").then((m) => m.lifestyleRoutes),
  },
  // No Model/Product Group permission yet — reuse the ProductOffering grant
  // (DSR placeholder).
  {
    path: ROUTE_SEGMENTS.MODEL_PRODUCT_GROUP,
    canActivate: [permissionGuard(Permissions.ModelProductGroupRead)],
    data: {
      menu: {
        label: "Model / Product Group",
        icon: "deployed_code",
        requiredPermission: Permissions.ModelProductGroupRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./model-product-group/model-product-group.routes").then(
        (m) => m.modelProductGroupRoutes,
      ),
  },
  // Device: Enrich is the Online Device editor (its own menu until
  // 2026-10-26); Image is the device image workspace.
  {
    path: ROUTE_SEGMENTS.DEVICE,
    data: {
      menu: {
        label: "Device",
        icon: "devices",
        group: true,
      } satisfies MenuMeta,
    },
    children: [
      {
        path: "",
        redirectTo: ROUTE_SEGMENTS.ENRICH,
        pathMatch: "full",
      },
      {
        path: ROUTE_SEGMENTS.ENRICH,
        canActivate: [permissionGuard(Permissions.OnlineStoreRead)],
        data: {
          menu: {
            label: "Enrich",
            dashboardLabel: "Device Enrich",
            icon: "auto_awesome",
            requiredPermission: Permissions.OnlineStoreRead,
          } satisfies MenuMeta,
        },
        loadChildren: () =>
          import("./online-store/online-store.routes").then(
            (m) => m.onlineStoreRoutes,
          ),
      },
      // Front-end only until the API exists, so it ships behind a flag.
      // No Device Image permission yet — reuse the Online Device read grant.
      {
        path: ROUTE_SEGMENTS.IMAGE,
        canActivate: [
          featureFlagGuard("enableDeviceImage"),
          permissionGuard(Permissions.OnlineStoreRead),
        ],
        data: {
          menu: {
            label: "Image",
            dashboardLabel: "Device Image",
            icon: "image",
            requiredFeatureFlag: "enableDeviceImage",
            requiredPermission: Permissions.OnlineStoreRead,
          } satisfies MenuMeta,
        },
        loadChildren: () =>
          import("./device/device-image.routes").then(
            (m) => m.deviceImageRoutes,
          ),
      },
    ],
  },
  // The old Online Device address. Bookmarks, shared links and history
  // entries land on the same page under Device › Enrich, query kept.
  {
    path: ROUTE_SEGMENTS.ONLINE_STORE,
    children: [
      {
        path: "**",
        redirectTo: ({ url, queryParams, fragment }) =>
          inject(Router).createUrlTree(
            [
              APP_ROUTES.MANAGEMENT.ONLINE_STORE.ROOT,
              ...url.map((s) => s.path),
            ],
            { queryParams, fragment: fragment ?? undefined },
          ),
      },
    ],
  },
  {
    path: ROUTE_SEGMENTS.PACKAGE,
    canActivate: [
      anyPermissionGuard([
        Permissions.EasyAppPackageRead,
        Permissions.MyChannelPackageMainRead,
      ]),
    ],
    data: {
      menu: {
        label: "Package",
        icon: "package_2",
        group: true,
        requiredAnyPermission: [
          Permissions.EasyAppPackageRead,
          Permissions.MyChannelPackageMainRead,
        ],
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./package/package.routes").then((m) => m.packageRoutes),
  },
  {
    path: ROUTE_SEGMENTS.POLICY,
    canActivate: [permissionGuard(Permissions.PolicyRead)],
    data: {
      menu: {
        label: "Policy",
        icon: "policy",
        requiredPermission: Permissions.PolicyRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("../../feature/management/policy/policy.routes").then(
        (m) => m.POLICY_ROUTES,
      ),
  },
  {
    path: ROUTE_SEGMENTS.PROMOTION,
    canActivate: [permissionGuard(Permissions.PromotionByLocationRead)],
    data: {
      menu: {
        label: "Promotion",
        icon: "sell",
        group: true,
        requiredPermission: Permissions.PromotionByLocationRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./promotion/promotion.routes").then((m) => m.promotionRoutes),
  },
  {
    path: ROUTE_SEGMENTS.ROM,
    canActivate: [permissionGuard(Permissions.RomPackageRead)],
    data: {
      menu: {
        label: "ROM",
        icon: "dot",
        requiredPermission: Permissions.RomPackageRead,
      } satisfies MenuMeta,
    },
    loadChildren: () => import("./rom/rom.routes").then((m) => m.romRoutes),
  },
  {
    path: ROUTE_SEGMENTS.CATALOG,
    canActivate: [permissionGuard(Permissions.CatalogRead)],
    loadChildren: () =>
      import("./catalog/catalog.route").then((m) => m.catalogRoutes),
  },
  {
    path: ROUTE_SEGMENTS.CATEGORY,
    canActivate: [permissionGuard(Permissions.CategoryRead)],
    data: {
      menu: {
        label: "Shelf",
        icon: "category",
        requiredPermission: Permissions.CategoryRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./category/category.route").then((m) => m.categoryRoutes),
  },
  {
    path: ROUTE_SEGMENTS.VDO,
    data: {
      menu: {
        label: "VDO",
        icon: "auto_awesome",
        group: true,
      } satisfies MenuMeta,
    },
    children: [
      {
        path: ROUTE_SEGMENTS.VDO_ENRICHMENT,
        canActivate: [
          featureFlagGuard(VDO_SHELF_FLAG),
          permissionGuard(Permissions.VdoPackageRead),
        ],
        data: {
          menu: {
            label: "Package",
            dashboardLabel: "Package VDO",
            icon: "package_2",
            requiredFeatureFlag: VDO_SHELF_FLAG,
            requiredPermission: Permissions.VdoPackageRead,
          } satisfies MenuMeta,
        },
        loadChildren: () =>
          import("./vdo-enrichment/vdo-enrichment.routes").then(
            (m) => m.vdoEnrichmentRoutes,
          ),
      },
      {
        path: ROUTE_SEGMENTS.VDO_CAMPAIGN,
        canActivate: [
          featureFlagGuard(VDO_CAMPAIGN_FLAG),
          permissionGuard(Permissions.VdoCampaignRead),
        ],
        providers: [VdoChannelResolver],
        data: {
          menu: {
            label: "Campaign",
            dashboardLabel: "Campaign VDO",
            icon: "campaign",
            requiredFeatureFlag: VDO_CAMPAIGN_FLAG,
            requiredPermission: Permissions.VdoCampaignRead,
          } satisfies MenuMeta,
        },
        loadChildren: () =>
          import("./vdo-campaign/vdo-campaign.routes").then(
            (m) => m.vdoCampaignRoutes,
          ),
      },
    ],
  },
  // No DSR upload permission yet — reuse the Blob grant (see uploads.routes.ts).
  {
    path: ROUTE_SEGMENTS.UPLOADS,
    canActivate: [permissionGuard(Permissions.BlobRead)],
    data: {
      menu: {
        label: "Uploads",
        icon: "cloud_upload",
        group: true,
        requiredPermission: Permissions.BlobRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("./uploads/uploads.routes").then((m) => m.uploadsRoutes),
  },
  {
    path: ROUTE_SEGMENTS.USSD_V2,
    canActivate: [permissionGuard(Permissions.USSDRead)],
    data: {
      menu: {
        label: "USSD",
        icon: "dialpad",
        requiredPermission: Permissions.USSDRead,
      } satisfies MenuMeta,
    },
    loadChildren: () =>
      import("@app/feature/management/ussd-v2/ussd-v2.routes").then(
        (m) => m.ussdV2Routes,
      ),
  },
];
